import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, Send, Users, Eye, Sparkles, Wand2, Loader2, Clock, FileText,
  ChevronRight, User, Zap, Check
} from 'lucide-react';
import { JewelButton, JewelInput, JewelTextarea, JewelField } from '../../components/ui/jewel-modal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_TEMPLATES = [
  { id: 'monthly', name: 'Monthly Update', subject: 'This month at Starnight — {{month}}',
    body: "Hi {{name}},\n\nHere's what happened this month at Starnight Holdings:\n\n• [Highlight one]\n• [Highlight two]\n• [Highlight three]\n\nAs always, thanks for being part of the journey.\n\nWith gratitude,\nThe Starnight team" },
  { id: 'launch',  name: 'Product/Feature Launch', subject: 'Introducing [feature] — a new way to [benefit]',
    body: "Hi {{name}},\n\nToday we're launching something we've been quietly building.\n\n[Feature name] is designed to [benefit]. It means you can now [primary use case] without [old friction].\n\nExplore it here: [link]\n\nWe'd love to hear what you think." },
  { id: 'event',   name: 'Event Invitation', subject: "You're invited: [event name] on [date]",
    body: "Hi {{name}},\n\nWe're hosting [event name] on [date] and would love to see you there.\n\nWhat to expect:\n• [Speaker/session one]\n• [Speaker/session two]\n\nRSVP here: [link]" },
  { id: 'thanks',  name: 'Thank You', subject: 'A quick thank you',
    body: "Hi {{name}},\n\nJust a note to say thank you for being with us. Your support means the world.\n\n— The Starnight team" },
];

const NewsletterPage = ({ getAuthHeaders, currentUser }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);

  const canManage = ['executive', 'superadmin'].includes(currentUser?.role);

  useEffect(() => {
    if (!canManage) return;
    fetchSubscribers();
    fetchHistory();
  }, [canManage]);

  if (!canManage) {
    return <div className="flex items-center justify-center h-96"><p className="text-white/40">Access denied. Executives & Superadmins only.</p></div>;
  }

  const fetchSubscribers = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/admin/user-accounts`, { headers });
      setSubscriberCount((res.data || []).filter(u => u.subscribed_to_newsletter).length);
    } catch {}
  };

  const fetchHistory = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/users/newsletter-history`, { headers });
      setHistory(res.data || []);
    } catch {}
  };

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setBody(t.body);
    toast.success(`Loaded: ${t.name}`);
  };

  const runAI = async (mode) => {
    if (mode === 'body' && !subject.trim()) {
      toast.error('Add a subject first — AI needs context');
      return;
    }
    setAiBusy(mode);
    try {
      const headers = getAuthHeaders();
      // Reuse the news AI endpoint — same prompt shape works
      const promptTitle = mode === 'subject' ? body.substring(0, 200) : subject;
      const res = await axios.post(`${BACKEND_URL}/api/admin/news/ai-assist`, {
        mode: mode === 'body' ? 'content' : mode === 'subject' ? 'title' : 'polish',
        title: promptTitle,
        category: 'Newsletter',
        current_content: body,
        context: 'Email newsletter for subscribers of Starnight Holdings',
      }, { headers });
      if (mode === 'body') {
        setBody(res.data.text);
        toast.success('Body drafted');
      } else if (mode === 'subject') {
        const first = (res.data.items || [])[0];
        if (first) { setSubject(first); toast.success('Subject suggested'); }
      } else if (mode === 'polish') {
        setBody(res.data.text);
        toast.success('Content polished');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI failed');
    }
    setAiBusy(null);
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message required'); return; }
    if (!window.confirm(`Deliver newsletter to ${subscriberCount} subscriber inboxes?`)) return;
    setSending(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/users/send-newsletter`, {
        subject, content: body
      }, { headers });
      toast.success(res.data?.message || `Delivered to ${subscriberCount} inboxes`);
      setSubject(''); setBody('');
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    }
    setSending(false);
  };

  return (
    <div className="space-y-6 relative" data-testid="newsletter-page">
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
        <div className="absolute -top-32 left-1/4 w-[500px] h-[300px] bg-cyan-500/[0.08] rounded-full blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[400px] h-[280px] bg-violet-500/[0.06] rounded-full blur-[120px]" />
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0a0a14] via-[#0d0b1f] to-[#0a0a14] p-6 md:p-7">
        <div className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] bg-cyan-500/[0.10] rounded-full blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-violet-500/[0.08] rounded-full blur-[100px]" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="relative w-14 h-14 flex-shrink-0">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-600 shadow-lg shadow-cyan-500/30" />
              <div className="absolute inset-0 rounded-2xl flex items-center justify-center">
                <Mail className="w-7 h-7 text-white" strokeWidth={1.5} />
              </div>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/70 font-medium mb-1">Content · Newsletter</p>
              <h1 className="text-2xl md:text-3xl font-semibold text-white tracking-tight leading-tight">
                Newsletter <span className="bg-gradient-to-r from-cyan-300 to-violet-300 bg-clip-text text-transparent">Studio</span>
              </h1>
              <p className="text-white/50 text-sm mt-1">Compose · templates · AI-drafted content · delivered straight to subscriber inboxes</p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-2xl bg-white/[0.02] border border-white/[0.06] px-4 py-3">
            <Users className="w-4 h-4 text-cyan-300" />
            <div>
              <p className="text-2xl font-light text-white tabular-nums leading-none">{subscriberCount}</p>
              <p className="text-[9px] tracking-[0.15em] uppercase text-white/40 mt-1">Subscribers</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: template library */}
        <aside className="lg:col-span-1 space-y-3">
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-3.5 h-3.5 text-violet-300" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-violet-200/80 font-semibold">Quick templates</p>
            </div>
            <div className="space-y-1.5">
              {PRESET_TEMPLATES.map(t => (
                <button key={t.id} onClick={() => applyTemplate(t)}
                  data-testid={`newsletter-template-${t.id}`}
                  className="w-full text-left p-3 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-violet-400/40 hover:bg-white/[0.05] transition-all group">
                  <p className="text-white text-[12.5px] font-medium">{t.name}</p>
                  <p className="text-white/40 text-[10.5px] mt-0.5 truncate group-hover:text-white/60">{t.subject}</p>
                </button>
              ))}
            </div>
          </div>

          {/* History */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-3.5 h-3.5 text-emerald-300" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-emerald-200/80 font-semibold">History</p>
              {history.length > 0 && <span className="text-white/40 text-[10px]">· {history.length}</span>}
            </div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto jewel-scroll pr-1">
              {history.length === 0 ? (
                <p className="text-white/30 text-xs italic text-center py-4">No newsletters sent yet</p>
              ) : history.map(h => (
                <div key={h.id} className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05]" data-testid={`newsletter-history-${h.id}`}>
                  <p className="text-white text-[12px] font-medium truncate">{h.subject}</p>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-white/40">
                    <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{h.recipient_count}</span>
                    <span>· {new Date(h.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* RIGHT: composer OR preview */}
        <main className="lg:col-span-2 space-y-3">
          {!showPreview ? (
            <div className="rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.03] to-violet-500/[0.03] p-5 space-y-4" data-testid="newsletter-composer">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-cyan-300" />
                  <p className="text-[10px] tracking-[0.2em] uppercase text-cyan-200/80 font-semibold">Compose</p>
                </div>
                <button type="button" onClick={() => setShowPreview(true)} disabled={!subject && !body}
                  data-testid="newsletter-preview-toggle"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/70 hover:text-white hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed text-[11px] font-medium transition-all">
                  <Eye className="w-3 h-3" /> Preview
                </button>
              </div>

              <JewelField label="Subject" required>
                <div className="flex gap-2">
                  <JewelInput value={subject} onChange={(e) => setSubject(e.target.value)}
                    placeholder="A crisp email subject line…"
                    data-testid="newsletter-subject" />
                  <AIBtn mode="subject" busy={aiBusy} onClick={() => runAI('subject')} icon={Wand2} label="AI" />
                </div>
              </JewelField>

              <JewelField label={`Body${body ? ` · ${body.trim().split(/\s+/).length} words` : ''}`} required>
                <div className="relative">
                  <JewelTextarea rows={12} value={body} onChange={(e) => setBody(e.target.value)}
                    placeholder="Write your newsletter here. Use {{name}} to personalise…"
                    data-testid="newsletter-body" />
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  <AIBtn mode="body" busy={aiBusy} onClick={() => runAI('body')} icon={Wand2} label={body ? 'Regenerate' : 'AI draft'} />
                  {body && <AIBtn mode="polish" busy={aiBusy} onClick={() => runAI('polish')} icon={Sparkles} label="Polish" />}
                </div>
              </JewelField>

              <div className="flex justify-end pt-3 border-t border-white/[0.06]">
                <JewelButton accent="cyan" onClick={send} loading={sending} disabled={sending || !subject.trim() || !body.trim()} icon={Send} data-testid="newsletter-send-btn">
                  Deliver to {subscriberCount} inbox{subscriberCount === 1 ? '' : 'es'}
                </JewelButton>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-cyan-500/25 bg-[#0a0a14] overflow-hidden" data-testid="newsletter-preview">
              <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5 text-cyan-300" />
                  <p className="text-[10px] tracking-[0.2em] uppercase text-cyan-200/80 font-semibold">Preview · as subscribers see it</p>
                </div>
                <button type="button" onClick={() => setShowPreview(false)}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] text-white/70 hover:text-white hover:bg-white/[0.08] text-[11px] font-medium transition-all">
                  <FileText className="w-3 h-3" /> Back to edit
                </button>
              </div>
              <div className="p-6 bg-white/[0.01]">
                <div className="max-w-2xl mx-auto bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-white/[0.06] bg-white/[0.02] flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">S</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">Starnight Holdings <span className="text-white/40 font-normal">· In-app newsletter</span></p>
                      <p className="text-white/40 text-[11px]">to Subscriber inboxes</p>
                    </div>
                    <p className="text-white/40 text-[10px]">{new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                  </div>
                  <div className="px-5 py-4">
                    <h2 className="text-white text-xl font-semibold tracking-tight">{subject || <span className="text-white/30 italic">Subject preview</span>}</h2>
                    <div className="mt-4 text-white/80 text-[14px] leading-[1.7] whitespace-pre-wrap">
                      {body || <span className="text-white/30 italic">Your message preview will appear here</span>}
                    </div>
                    <div className="mt-6 pt-4 border-t border-white/[0.06] text-white/40 text-[11px] text-center">
                      Delivered to your Starnight Hub inbox.<br />
                      Manage preferences from <span className="text-cyan-300/70">Account Settings → Notifications</span>.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const AIBtn = ({ mode, busy, onClick, icon: Icon, label }) => {
  const b = busy === mode;
  return (
    <button type="button" onClick={onClick} disabled={!!busy}
      data-testid={`ai-newsletter-${mode}`}
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-400/30 text-violet-100 hover:from-violet-500/35 hover:to-cyan-500/35 hover:border-violet-400/50 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-semibold transition-all whitespace-nowrap">
      {b ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      {label}
      <Sparkles className="w-2.5 h-2.5 text-amber-300 opacity-70" />
    </button>
  );
};

export default NewsletterPage;
